import { useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/utils/cn';

type FieldVariant = 'app' | 'onboarding';

type BaseFieldProps = {
  className?: string;
  labelClassName?: string;
  controlClassName?: string;
  wrapperClassName?: string;
  iconClassName?: string;
  errorClassName?: string;
  helpClassName?: string;
  variant?: FieldVariant;
  label?: string;
  helpText?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

type InputProps = BaseFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
    as?: 'input';
  };

type TextareaProps = BaseFieldProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
    as: 'textarea';
  };

type SelectProps = BaseFieldProps &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
    as: 'select';
  };

type InputFormProps = InputProps | TextareaProps | SelectProps;

function getFieldClassName(className?: string, variant?: FieldVariant) {
  return cn('field', variant === 'onboarding' && 'field--onboarding', className);
}

function renderFeedback(error?: string, errorClassName?: string, helpText?: string, helpClassName?: string) {
  if (error) {
    return <span className={cn('field__error', errorClassName)}>{error}</span>;
  }

  if (helpText) {
    return <span className={cn('field__help', helpClassName)}>{helpText}</span>;
  }

  return null;
}

export function InputForm(props: InputFormProps) {
  if (props.as === 'textarea') {
    const {
      className,
      labelClassName,
      controlClassName,
      errorClassName,
      helpClassName,
      variant,
      label,
      helpText,
      error,
      leftIcon: _leftIcon,
      rightIcon: _rightIcon,
      ...controlProps
    } = props;

    return (
      <label className={getFieldClassName(className, variant)}>
        <span className={cn('field__label', labelClassName)}>{label}</span>
        <textarea className={cn('field__control', 'field__control--textarea', controlClassName)} {...controlProps} />
        {renderFeedback(error, errorClassName, helpText, helpClassName)}
      </label>
    );
  }

  if (props.as === 'select') {
    const {
      className,
      labelClassName,
      controlClassName,
      errorClassName,
      helpClassName,
      variant,
      label,
      helpText,
      error,
      leftIcon: _leftIcon,
      rightIcon: _rightIcon,
      children,
      ...controlProps
    } = props;

    return (
      <label className={getFieldClassName(className, variant)}>
        <span className={cn('field__label', labelClassName)}>{label}</span>
        <select className={cn('field__control', controlClassName)} {...controlProps}>
          {children}
        </select>
        {renderFeedback(error, errorClassName, helpText, helpClassName)}
      </label>
    );
  }

  const {
    className,
    labelClassName,
    controlClassName,
    wrapperClassName,
    iconClassName,
    errorClassName,
    helpClassName,
    variant,
    label,
    helpText,
    error,
    leftIcon,
    rightIcon,
    type,
    ...controlProps
  } = props;
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const canTogglePassword = type === 'password' && Boolean(rightIcon);
  const resolvedType = canTogglePassword && isPasswordVisible ? 'text' : type;

  const inputClassName = cn(
    'field__control',
    leftIcon && 'field__control--with-left-icon',
    rightIcon && 'field__control--with-right-icon',
    controlClassName,
  );

  return (
    <label className={getFieldClassName(className, variant)}>
      <span className={cn('field__label', labelClassName)}>{label}</span>
      <div className={cn('field__control-wrap', wrapperClassName)}>
        {leftIcon ? <span className={cn('field__icon', 'field__icon--left', iconClassName)} aria-hidden="true">{leftIcon}</span> : null}
        <input className={inputClassName} type={resolvedType} {...controlProps} />
        {rightIcon && canTogglePassword ? (
          <button
            type="button"
            className={cn('field__icon-button', 'field__icon-button--right', iconClassName)}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            aria-pressed={isPasswordVisible}
            onClick={() => setPasswordVisible((currentValue) => !currentValue)}
          >
            <span className="field__icon" aria-hidden="true">{rightIcon}</span>
          </button>
        ) : null}
        {rightIcon && !canTogglePassword ? (
          <span className={cn('field__icon', 'field__icon--right', iconClassName)} aria-hidden="true">{rightIcon}</span>
        ) : null}
      </div>
      {renderFeedback(error, errorClassName, helpText, helpClassName)}
    </label>
  );
}
